import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * The AI surface currently in context. One app-wide orb (in AppFooter) is driven by this
 * store: whichever page is active registers its context, and the orb opens the matching
 * drawer. The union is intentionally extensible — new AI surfaces add a `kind` (per the
 * app-wide context-aware orb pattern) without touching the footer/orb wiring.
 */
export type AiChatContext =
  | { kind: 'survey-builder'; surveyId: string }
  | { kind: 'brand-standards'; surveyId: string }

/**
 * Global AI chat context store. Pages `register()` a context on entry (enabling the footer
 * orb) and `clear()` it on leave. `open()`/`close()` drive the active page's drawer.
 */
export const useAiChatContextStore = defineStore('ai-chat-context', () => {
  const context = ref<AiChatContext | null>(null)
  const isOpen = ref(false)

  /** The orb is visible whenever a page has registered a context. */
  const isEnabled = computed(() => context.value !== null)

  function register(ctx: AiChatContext): void {
    const sameContext =
      context.value?.kind === ctx.kind &&
      (context.value as { surveyId?: string } | null)?.surveyId ===
        (ctx as { surveyId?: string }).surveyId
    context.value = ctx
    // A freshly-registered *different* context must never inherit a previous page's open
    // drawer — but re-registering the SAME context must NOT close an open drawer. The
    // survey-builder page's watch re-runs register() on every currentSurvey change, and a
    // silent refreshSurvey (after applying an AI action, #1046) changes currentSurvey; without
    // this guard that re-register would force-close the open chat drawer. (#1046)
    if (!sameContext) isOpen.value = false
  }

  function clear(): void {
    context.value = null
    isOpen.value = false
  }

  function open(): void {
    isOpen.value = true
  }

  function close(): void {
    isOpen.value = false
  }

  return {
    context,
    isOpen,
    isEnabled,
    register,
    clear,
    open,
    close,
  }
})
